import { createTheme, type ThemeOptions } from '@mui/material/styles';

import { APP_THEME_TOKENS, STYLE_TOKENS } from './tokens';

type ThemeOptionsWithTokens = ThemeOptions & {
  tokens: typeof APP_THEME_TOKENS;
};

export const MUI_THEME_OPTIONS: ThemeOptionsWithTokens = {
  tokens: APP_THEME_TOKENS,
  typography: {
    fontFamily: APP_THEME_TOKENS.foundation.font.family.body,
    fontSize: 14,
    h1: {
      fontFamily: APP_THEME_TOKENS.foundation.font.family.heading,
      fontSize: '2rem',
      fontWeight: APP_THEME_TOKENS.foundation.font.weight.bold,
      lineHeight: APP_THEME_TOKENS.foundation.font.lineHeight.tight,
    },
    h2: {
      fontFamily: APP_THEME_TOKENS.foundation.font.family.heading,
      fontSize: '1.5rem',
      fontWeight: APP_THEME_TOKENS.foundation.font.weight.bold,
      lineHeight: APP_THEME_TOKENS.foundation.font.lineHeight.tight,
    },
    h3: {
      fontFamily: APP_THEME_TOKENS.foundation.font.family.heading,
      fontSize: '1.25rem',
      fontWeight: APP_THEME_TOKENS.foundation.font.weight.semibold,
      lineHeight: APP_THEME_TOKENS.foundation.font.lineHeight.tight,
    },
    body1: {
      fontSize: APP_THEME_TOKENS.foundation.font.size.md,
      lineHeight: APP_THEME_TOKENS.foundation.font.lineHeight.normal,
    },
    body2: {
      fontSize: APP_THEME_TOKENS.foundation.font.size.sm,
      lineHeight: APP_THEME_TOKENS.foundation.font.lineHeight.normal,
    },
    caption: {
      fontSize: APP_THEME_TOKENS.foundation.font.size.xs,
      lineHeight: APP_THEME_TOKENS.foundation.font.lineHeight.normal,
    },
    button: {
      fontWeight: APP_THEME_TOKENS.foundation.font.weight.semibold,
      textTransform: 'none',
    },
  },
  palette: {
    text: {
      primary: STYLE_TOKENS.color.text.primary,
      secondary: STYLE_TOKENS.color.text.secondary,
      disabled: STYLE_TOKENS.color.text.disabled,
    },
    divider: STYLE_TOKENS.color.border.subtle,
    background: {
      default: STYLE_TOKENS.color.background.canvas,
      paper: STYLE_TOKENS.color.background.canvas,
    },
    primary: {
      main: STYLE_TOKENS.color.brand.primary,
      contrastText: STYLE_TOKENS.color.background.canvas,
    },
    secondary: {
      main: STYLE_TOKENS.color.brand.accent,
      contrastText: STYLE_TOKENS.color.background.canvas,
    },
    success: {
      main: STYLE_TOKENS.color.semantic.success,
    },
    info: {
      main: STYLE_TOKENS.color.semantic.info,
    },
    warning: {
      main: STYLE_TOKENS.color.semantic.warning,
    },
    error: {
      main: STYLE_TOKENS.color.semantic.danger,
    },
  },
  shape: {
    borderRadius: APP_THEME_TOKENS.foundation.radius.sm,
  },
  spacing: 4,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: STYLE_TOKENS.color.background.canvas,
          color: STYLE_TOKENS.color.text.primary,
          fontFamily: APP_THEME_TOKENS.foundation.font.family.body,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: APP_THEME_TOKENS.component.button.radius.sm,
          fontWeight: APP_THEME_TOKENS.foundation.font.weight.semibold,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variant: APP_THEME_TOKENS.component.typography.defaultVariant,
      },
    },
  },
};

export const createAppTheme = () => createTheme(MUI_THEME_OPTIONS as ThemeOptions);
export const theme = createAppTheme();
